import { useMutation as useValidatePhoneNumber } from "@faire/web-api/api/v2/addresses/validate-phone-number/post-hooks";
import { IValidatePhoneNumberRequest } from "@faire/web-api/indigofair/data/IValidatePhoneNumberRequest";
import { ISO3166Alpha3 } from "@faire/web-api/indigofair/iso3166/ISO3166Alpha3";
import { useStrictLocalization } from "@faire/web/common/localization";
import { getAddressSchema } from "@faire/web/ui/Addresses/addressSchema";

import { SampleForm } from "@faire/retailer/features/ProspectingWithSamples/ProspectingSampleSignupFlow";

export const useFormValidation = () => {
  const { strictLocalize } = useStrictLocalization();
  const { mutateAsync: validatePhoneNumber } = useValidatePhoneNumber();

  const validateForm = async (values: SampleForm) => {
    const errors: Partial<SampleForm> = {};
    const addressSchema = getAddressSchema(values.country, strictLocalize);

    if (values.firstName.length < 1) {
      errors.firstName = strictLocalize({
        defaultMessage: "First name is required",
        description: {
          text: "Error message when user does not enter a first name",
          img: "https://cdn.faire.com/fastly/475eb01aa15a265ff280cd849dff5fe88741a154d21172ac7f766db99ce8245b.png",
        },
      });
    }
    if (values.lastName.length < 1) {
      errors.lastName = strictLocalize({
        defaultMessage: "Last name is required",
        description: {
          text: "Error message when user does not enter a last name",
          img: "https://cdn.faire.com/fastly/475eb01aa15a265ff280cd849dff5fe88741a154d21172ac7f766db99ce8245b.png",
        },
      });
    }
    if (values.storeName.length < 1) {
      errors.storeName = strictLocalize({
        defaultMessage: "Store name is required",
        description: {
          text: "Error message when user does not enter a store name",
          img: "https://cdn.faire.com/fastly/475eb01aa15a265ff280cd849dff5fe88741a154d21172ac7f766db99ce8245b.png",
        },
      });
    }
    if (values.address1.length < 1) {
      errors.address1 = strictLocalize({
        defaultMessage: "Street address is required",
        description: {
          text: "Error message when user does not enter a street address",
          img: "https://cdn.faire.com/fastly/475eb01aa15a265ff280cd849dff5fe88741a154d21172ac7f766db99ce8245b.png",
        },
      });
    }
    if (values.city.length < 1) {
      errors.city = strictLocalize({
        defaultMessage: "City is required",
        description: {
          text: "Error message when user does not enter a city",
          img: "https://cdn.faire.com/fastly/475eb01aa15a265ff280cd849dff5fe88741a154d21172ac7f766db99ce8245b.png",
        },
      });
    }
    if (
      values.state.length < 1 &&
      addressSchema.required_fields.includes("state")
    ) {
      errors.state = strictLocalize({
        defaultMessage: "State is required",
        description: {
          text: "Error message when user does not enter a state",
          img: "https://cdn.faire.com/fastly/475eb01aa15a265ff280cd849dff5fe88741a154d21172ac7f766db99ce8245b.png",
        },
      });
    }
    if (values.zipcode.length < 1) {
      errors.zipcode = strictLocalize({
        defaultMessage: "Zipcode/Postal code is required",
        description: {
          text: "Error message when user does not enter a zipcode",
          img: "https://cdn.faire.com/fastly/475eb01aa15a265ff280cd849dff5fe88741a154d21172ac7f766db99ce8245b.png",
        },
      });
    }
    // validation runs on every single field change, we validate phone number
    // only when there are no other errors:
    if (Object.keys(errors).length === 0) {
      // values.phoneNumber includes country code and "space". e.g. "+1 1234567890"
      // Outside US/CAN all numbers include 4 characters for country code
      // we expect at least 5 digits after country code:
      if (values.phoneNumber.length > 8) {
        const { is_valid } = await validatePhoneNumber([
          IValidatePhoneNumberRequest.build({
            phone_number: values.phoneNumber,
            country: values.country as ISO3166Alpha3,
          }),
        ]);
        if (!is_valid) {
          errors.phoneNumber = strictLocalize({
            defaultMessage: "Phone number is invalid",
            description: {
              text: "Error message when user enters an invalid phone number",
              img: "https://cdn.faire.com/fastly/a92621a7ce51c68e345ba7fa095de9e8cf44aeaae5e7773170cbd01827588085.png",
            },
          });
        }
      } else {
        errors.phoneNumber = strictLocalize({
          defaultMessage: "Phone number is invalid",
          description: {
            text: "Error message when user enters an invalid phone number",
            img: "https://cdn.faire.com/fastly/a92621a7ce51c68e345ba7fa095de9e8cf44aeaae5e7773170cbd01827588085.png",
          },
        });
      }
    }
    return errors;
  };

  return { validateForm };
};
